<template>
  <app-page
    title="Employee Opt Out"
    :error="error"
    :previous="[
      { label: 'Employees', to: { name: 'employees' } },
      {
        label: employee ? employee.full_name : 'Employee',
        to: { name: 'employee', params: { employeeId: $route.params.employeeId } },
      },
    ]"
    icon="user-minus"
  >
    <app-loading :loading="loading" />
    <template v-if="!loading && !success && employee">
      <!-- Fail-closed: we could not establish whether the employee still holds products, so
           opting out is unavailable rather than allowed by default. -->
      <sme-alert v-if="eligibilityError" level="danger" class="mb-3" data-testid="optout-eligibility-error">
        There was an error loading the opt-out page. Please try again, or
        <a href="#" @click.prevent="contactSupport">contact support</a>
        if this continues.
      </sme-alert>

      <template v-else-if="blocked">
        <sme-alert level="warning" class="mb-3" data-testid="optout-blocked">
          <p>
            <strong>{{ employee.full_name }} cannot be opted out yet.</strong>
          </p>
          <p>
            This employee holds active products with {{ partnershipName }}, such as savings or a loan. Opting out would
            remove their app access and leave them unable to reach those products.
          </p>
        </sme-alert>

        <p v-if="canRecordLeaver">
          If {{ employee.full_name }}'s employment is ending, please
          <router-link :to="{ name: 'employee-terminate', params: { employeeId: employee.employee_id } }"
            >mark them as a leaver</router-link
          >
          instead. Employees who are leaving your business will no longer have access to flexible pay but retain the app
          access they need to reach active products.
        </p>
        <p v-else>
          Please <a href="#" @click.prevent="contactSupport">contact support</a> if you need to disable app access and
          unenrol {{ employee.full_name }}.
        </p>

        <sme-alert v-if="!canRecordLeaver && canPauseEmployee(employee)" level="info" class="mb-0">
          If you want to instantly restrict {{ employee.full_name }} from accessing their flexible pay, you can
          <router-link :to="{ name: 'employee-pause', params: { employeeId: employee.employee_id } }"
            >pause their access to flexible pay</router-link
          >.
        </sme-alert>
      </template>

      <template v-else>
        <sme-alert level="warning" class="mb-3">
          You are about to remove access to the app for {{ employee.full_name }}
        </sme-alert>

        <p class="text-muted">
          {{ employee.full_name }} will lose all access to the {{ partnershipName }} app and
          <strong>will not be able to log in</strong>. This means they
          <strong>may be unable to access other products they actively hold with {{ partnershipName }}</strong
          >, such as savings, loans and pensions.
        </p>
      </template>

      <sme-alert v-if="canPauseEmployee(employee) && !blocked" level="info" class="mb-3">
        If you want to temporarily stop {{ employee.full_name }} accessing their earned wages but not remove their
        access to the app,
        <router-link :to="{ name: 'employee-pause', params: { employeeId: employee.employee_id } }"
          >pause their access to flexible pay</router-link
        >
        instead.
      </sme-alert>

      <ValidationObserver v-if="!blocked && !eligibilityError" v-slot="v">
        <b-form @submit.prevent="v.handleSubmit(onSubmit)">
          <app-input
            v-model="reason"
            type="text"
            name="reason"
            label="Reason for opting out"
            rules="required"
            :validate-immediate="false"
          />
          <app-input
            v-model="leaver"
            v-if="canDisableEmployees"
            type="checkbox-single"
            name="leaver"
            label="Is the employee leaving the company?"
            rules="required"
            :validate-immediate="false"
          />
          <app-input
            v-if="leaver && !shouldTerminateInstead"
            v-model="terminationDate"
            type="date"
            name="terminationDate"
            label="When is their last day of employment?"
            rules="required"
            :validate-immediate="false"
          />

          <!-- If Terminating is enabled, prompt the admin to take this route when applicable -->
          <sme-alert v-if="shouldTerminateInstead" level="info" class="mb-0" data-testid="optout-terminate-instead">
            <p>
              <strong>Mark {{ employee.full_name }} as leaver instead of opting them out.</strong>
            </p>
            <p class="mb-0">
              If {{ employee.full_name }}'s <strong>employment is ending</strong>, opting them out is not the right
              action as they will lose app access.
              <router-link :to="{ name: 'employee-terminate', params: { employeeId: employee.employee_id } }"
                >Enter their final date of employment</router-link
              >
              to end access to flexible pay while keeping the app access they need to reach any savings, loans or
              pensions they hold with {{ partnershipName }}.
            </p>
          </sme-alert>
          <page-sub-footer v-else>
            <b-button type="submit" variant="danger" :disabled="v.invalid || saving">
              <b-spinner v-if="saving" class="mr-2" small />
              {{ saving ? 'Opting out...' : 'Opt out' }}
            </b-button>
          </page-sub-footer>
        </b-form>
      </ValidationObserver>
    </template>
    <div v-if="success">
      <sme-alert level="good">
        <p>
          <strong>Employee has been deactivated.</strong>
        </p>
        <p>
          We have notified the employee that they no longer have access to the app. We have not shared the reason. If
          you would like to, you can email {{ employee.full_name }}
          at
          <a :href="toMailtoHref(employee.work_email)">{{ employee.work_email }}</a>
          to let them know more information.
        </p>
      </sme-alert>
      <div v-if="!completedOptOut" class="mt-3">
        <template v-if="readyToRevertBanking">
          <sme-alert level="info" class="mb-3">
            To complete opt-out you will need to change the employee's bank details in your payroll system.
          </sme-alert>
          <div class="d-flex justify-content-center">
            <router-link
              tag="button"
              class="btn btn-primary"
              :to="{ name: 'employee-revert-banking', params: { employeeId: employee.employee_id } }"
              >Complete Opt-Out</router-link
            >
          </div>
        </template>
        <sme-alert v-else level="info">
          When the employee has zero outstanding transfers you will be required to update their banking details to
          complete the opt-out process.
        </sme-alert>
      </div>
    </div>
  </app-page>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import ApiClient from '@/ApiClient';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppPage from '@/components/AppPage.vue';
import PageSubFooter from '@/components/PageSubFooter.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import State from '@/state/State';
import { toMailtoHref } from '@/utils/common';
import { DEFAULT_PARTNERSHIP_NAME, getPartnershipData } from '@/utils/partnership';
import { canPauseEmployee, canTerminateEmployeesAtAll } from '@/utils/Employee';
import { contactSupport } from '@/utils/support';

export default {
  name: 'EmployeeOptOut',
  components: { ValidationObserver, AppInput, AppLoading, AppPage, PageSubFooter, SmeAlert },
  data() {
    return {
      loading: true,
      error: undefined,
      saving: false,
      success: false,
      reason: undefined,
      leaver: false,
      terminationDate: undefined,
      completedOptOut: undefined,
      employee: undefined,
      everEnrolled: undefined,
      eligibility: undefined,
      eligibilityError: undefined,
      state: State.state,
    };
  },
  mounted() {
    this.loadData();
  },
  setup() {
    const { canDisableEmployees } = useFeatureFlags();
    const { name: partnershipName } = getPartnershipData();

    return {
      canDisableEmployees,
      partnershipName: partnershipName || DEFAULT_PARTNERSHIP_NAME,
    };
  },
  methods: {
    toMailtoHref,
    canPauseEmployee,
    contactSupport,
    async onSubmit() {
      this.saving = true;

      try {
        await ApiClient.employeeOptOut(this.employee.employee_id, { reason: this.reason });

        if (!this.everEnrolled) {
          try {
            await ApiClient.employeeOptOutComplete(this.employee.employee_id);
            this.completedOptOut = true;
          } catch (error) {
            this.error = error.message;
          }
        }

        if (this.leaver) {
          try {
            await ApiClient.employeeSetTerminationDate(this.employee.employee_id, this.terminationDate);
          } catch (error) {
            this.error = error.message;
          }
        }
      } catch (error) {
        this.error = error.message;
      }

      await ApiClient.getUser();
      this.success = true;
      this.saving = false;
    },
    async loadData() {
      this.loading = true;
      const employeeId = this.$route.params.employeeId;

      // Page stays in loading state until all three requests settle
      const [employee, eligibility] = await Promise.all([
        ApiClient.getEmployee(employeeId),
        this.checkEligibility(employeeId),
        this.checkifEverEnrolled(employeeId),
      ]).catch(error => {
        if (!this._isDestroyed) {
          this.error = error.message;
        }
        return [];
      });

      // The admin can navigate away while these are in flight, in which case there is no longer an
      // instance to write to.
      if (this._isDestroyed) {
        return;
      }

      this.employee = employee;
      this.eligibility = eligibility;
      this.loading = false;
    },
    // The regulated products this guards against are UK-only, and the eligibility endpoint has no
    // US mirror, so asking would 404 and fail the page closed for every US admin.
    checkEligibility(employeeId) {
      if (!useFeatureFlags().isUkCompany.value) {
        return undefined;
      }

      return ApiClient.getEmployeeOptOutEligibility(employeeId).catch(error => {
        if (!this._isDestroyed) {
          this.eligibilityError = error.message || 'Unable to check opt-out eligibility.';
        }
        return undefined;
      });
    },
    toMailTo(email) {
      return `mailto:${email}`;
    },
    checkifEverEnrolled(employeeId) {
      return ApiClient.checkEmployeeEverEnrolled(employeeId).then(
        everEnrolled => {
          if (!this._isDestroyed) {
            this.everEnrolled = everEnrolled;
          }
        },
        error => {
          if (!this._isDestroyed) {
            this.error = error.message;
          }
        },
      );
    },
  },
  computed: {
    blocked() {
      return Boolean(this.eligibility?.has_regulated_product);
    },
    canRecordLeaver() {
      return canTerminateEmployeesAtAll();
    },
    // When leaver checkbox ticked, push admin to the dedicated Terminate flow if available
    shouldTerminateInstead() {
      return this.leaver && this.canRecordLeaver;
    },
    readyToRevertBanking() {
      if (!this.employee) {
        return false;
      }
      const todos = State.state.todos;
      return todos.some(todo => todo.action === 'employee.revertbanking' && todo.fk === this.employee.employee_id);
    },
  },
};
</script>
